import clientLogo from "@assets/images/Illustration-mediamix.svg";
import ConfirmationDialog, {
  ConfirmationDialogButtonColor
} from "@components/ConfirmationDialog";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import { ROUTING_SIMULATOR } from "@constants/routing";
import {
  ROUTING_BENCHMARK,
  ROUTING_NEW_SIMULATION,
  ROUTING_SIMULATION
} from "@constants/simulatorRouting";
import { ClientContext } from "@context/Client/ClientContext";
import LayoutSelectors from "@layouts/LayoutSelectors";
import AddchartIcon from "@mui/icons-material/Addchart";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  useModelDeleteScenario,
  useModelPatchScenario,
  useModelScenarios
} from "@services/api";
import { ModelScenarioShort } from "@services/api/types";
import { toLocaleString } from "@utils/numbers";
import { TFunction } from "i18next";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ConfirmationDialogStatus {
  open: boolean;
  title: string;
  buttonColor: ConfirmationDialogButtonColor;
}

const toTableRow = (
  t: TFunction<"translation", undefined>,
  scenario: ModelScenarioShort,
  highlight: boolean = false,
  onClickRow: (scenarioId: number) => void,
  onClickDeleteRow: (scenarioId: number) => void,
  onClickFavorite: (scenarioId: number, isFavorite: boolean) => void,
  onClickForecast: (scenarioId: number) => void
): JSX.Element => {
  const fontWeight = highlight ? "bold" : "normal";

  return (
    <StyledTableRow
      key={scenario.id}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 }
      }}
    >
      <StyledTableCell component="th" scope="row" sx={{ fontWeight }}>
        <Box className="flex">
          <Tooltip title={t("common.delete")}>
            <IconButton onClick={() => onClickDeleteRow(scenario.id)}>
              <DeleteIcon color="error" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("simulator.scenarios.table.favorite")}>
            <IconButton
              onClick={() =>
                onClickFavorite(scenario.id, !scenario.is_favorite)
              }
            >
              {scenario.is_favorite ? (
                <StarIcon sx={{ color: "#f5f107" }} />
              ) : (
                <StarOutlineIcon />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={t("simulator.scenarios.table.forecast_selected")}>
            <IconButton onClick={() => onClickForecast(scenario.id)}>
              {scenario.forecast_selected ? (
                <CheckBoxIcon color="info" />
              ) : (
                <CheckBoxOutlineBlankIcon color="info" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </StyledTableCell>
      <StyledTableCell
        align="center"
        sx={{ fontWeight, cursor: "pointer" }}
        onClick={() => onClickRow(scenario.id)}
      >
        {scenario.name}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {scenario.time_period}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {scenario.year}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(scenario.media_investment, 0)}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(scenario.predicted_inflow, 0)}
      </StyledTableCell>
    </StyledTableRow>
  );
};

const ViewScenarios = () => {
  const { currentClient } = useContext(ClientContext);
  const { t } = useTranslation();
  const [pageSelected, setPageSelected] = useState("scenarios");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [scenarioIdToDelete, setScenarioIdToDelete] = useState<number | null>(
    null
  );
  const iconVisibility = isMobile ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "";

  const [confirmationDialogStatus, setConfirmationDialogStatus] =
    useState<ConfirmationDialogStatus>({
      open: false,
      title: "",
      buttonColor: "error"
    });

  const handlePageSelectionChange = (
    _: React.MouseEvent<HTMLElement>,
    newChartSelected: string
  ) => {
    setPageSelected(newChartSelected);
  };

  const handleTableRowClick = useCallback(
    (scenarioId: number) => {
      navigate(
        `${ROUTING_SIMULATOR}${ROUTING_SIMULATION.replace(":id", scenarioId.toString())}`
      );
    },
    [navigate]
  );

  const handleNewScenarioClick = () => {
    navigate(`${ROUTING_SIMULATOR}${ROUTING_NEW_SIMULATION}`);
  };

  const {
    data,
    isFetching: isLoading,
    refetch: refetchScenarios
  } = useModelScenarios(currentClient.model.id);

  const patchMutation = useModelPatchScenario();

  const handleScenarioFavorite = useCallback(
    (scenarioId: number, isFavorite: boolean) => {
      patchMutation.mutate(
        {
          scenario_id: scenarioId.toString(),
          data: { is_favorite: isFavorite }
        },
        {
          onSuccess: () => {
            refetchScenarios();
          }
        }
      );
    },
    [patchMutation, refetchScenarios]
  );

  const handleScenarioForecast = useCallback(
    (scenarioId: number) => {
      patchMutation.mutate(
        {
          scenario_id: scenarioId.toString(),
          data: { forecast_selected: true }
        },
        {
          onSuccess: () => {
            refetchScenarios();
          }
        }
      );
    },
    [patchMutation, refetchScenarios]
  );

  const deleteMutation = useModelDeleteScenario();

  const handleScenariodelete = useCallback(
    (scenarioId: number) => {
      setConfirmationDialogStatus({
        open: true,
        title: t("simulator.scenario.delete_scenario"),
        buttonColor: "error"
      });
      setScenarioIdToDelete(scenarioId);
    },
    [setScenarioIdToDelete]
  );

  const onCloseConfirmationDialog = () => {
    setConfirmationDialogStatus({
      open: false,
      title: "",
      buttonColor: "error"
    });
  };

  const onConfirmConfirmationDialog = () => {
    if (!scenarioIdToDelete) {
      return;
    }

    deleteMutation.mutate(
      {
        scenario_id: scenarioIdToDelete.toString()
      },
      {
        onSuccess: () => {
          setScenarioIdToDelete(null);
          setConfirmationDialogStatus({
            open: false,
            title: "",
            buttonColor: "error"
          });

          refetchScenarios();
        }
      }
    );
  };

  const tableData = useMemo(() => {
    return (data || [])
      .sort((a, b) => a.id - b.id)
      .map((scenarioData) => {
        return toTableRow(
          t,
          scenarioData,
          false,
          handleTableRowClick,
          handleScenariodelete,
          handleScenarioFavorite,
          handleScenarioForecast
        );
      });
  }, [
    t,
    data,
    handleTableRowClick,
    handleScenariodelete,
    handleScenarioFavorite
  ]);

  return (
    <LayoutSelectors>
      <Box className="w-full pt-4 pl-8 pr-8" sx={{ height: "100%" }}>
        <Box className="w-full flex pb-4">
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {t("navbar.tabs.simulator")}
          </Typography>
          <ArrowForwardIosIcon className="pt-1" />
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {t("simulator.pages.scenarios")}
          </Typography>
        </Box>
        <Card className="w-full">
          <CardContent>
            <Box className="w-full flex">
              <Box className="w-3/4 pl-4">
                <ToggleButtonGroup
                  color="primary"
                  value={pageSelected}
                  exclusive
                  onChange={handlePageSelectionChange}
                  aria-label="Pages"
                  disabled={isLoading}
                >
                  <ToggleButton
                    value="scenarios"
                    sx={{ textTransform: "none" }}
                    onClick={() => navigate(ROUTING_SIMULATOR)}
                  >
                    {t("simulator.pages.scenarios")}
                  </ToggleButton>
                  <ToggleButton
                    value="benchmark"
                    sx={{ textTransform: "none" }}
                    onClick={() =>
                      navigate(`${ROUTING_SIMULATOR}${ROUTING_BENCHMARK}`)
                    }
                  >
                    {t("simulator.pages.benchmark")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box className="w-1/4 text-right pr-4">
                <Tooltip title={t("simulator.scenarios.new_scenario")}>
                  <IconButton
                    color="info"
                    onClick={handleNewScenarioClick}
                    disabled={isLoading || !currentClient.model.id}
                  >
                    <AddchartIcon
                      sx={{
                        backgroundColor: "white",
                        fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box className="w-full">
              <Box
                className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}
              >
                <SwipeIcon
                  sx={{
                    bottom: 12,
                    opacity: 0.4,
                    left: "50%",
                    transform: "translate(750%, 0%)",
                    color: theme.palette.primary.main
                  }}
                  fontSize="small"
                />
              </Box>
              <TableContainer
                className="p-4"
                sx={{
                  overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
                  scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
                  "&::-webkit-scrollbar": {
                    display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
                  }
                }}
              >
                <Table className="w-full" aria-label="kpi calculator table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell align="center">
                        {t("simulator.scenarios.table.scenario")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {t("simulator.scenarios.table.period")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {t("simulator.scenarios.table.year")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {t("simulator.scenarios.table.media_spend", {
                          currency: currentClient.client.currency
                        })}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {t("simulator.scenarios.table.inflow", {
                          kpi: currentClient.client.kpi_label
                        })}
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRowsSkeleton
                        id={`scenarios-${currentClient.model.id}-table-skeleton`}
                        numberOfRows={5}
                        columns={6}
                      />
                    ) : (
                      tableData
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!isLoading &&
                currentClient.model.id.toString().length > 0 &&
                tableData.length === 0 && (
                  <Box className="w-full">
                    <Box sx={{ justifyItems: "center" }}>
                      <img
                        className="w-full md:w-[346px] max-w-full h-auto block"
                        src={clientLogo}
                        alt="MMix Logo"
                      />
                    </Box>

                    <Typography variant="h5" component="div">
                      <Box sx={{ textAlign: "center" }}>
                        {t("simulator.scenarios.no_scenarios.title")}
                      </Box>
                    </Typography>
                    <Typography variant="h6" component="div">
                      <Box sx={{ textAlign: "center" }}>
                        {t("simulator.scenarios.no_scenarios.description")}
                      </Box>
                    </Typography>
                  </Box>
                )}
            </Box>
          </CardContent>
        </Card>
        <ConfirmationDialog
          open={confirmationDialogStatus.open}
          onClose={onCloseConfirmationDialog}
          onConfirm={onConfirmConfirmationDialog}
          title={confirmationDialogStatus.title}
          buttonColor={confirmationDialogStatus.buttonColor}
        />
      </Box>
    </LayoutSelectors>
  );
};
export default ViewScenarios;
